import { motion } from 'framer-motion';
import {
  BookOpen,
  Edit3,
  FileText,
  HelpCircle,
  Link as LinkIcon,
  Plus,
  Save,
  Search,
  X,
  BookMarked,
  Hash,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';

interface WikiPage {
  id: string;
  entry_date: string | null;
  title: string | null;
  content: string;
  tags: string[];
  mood: string | null;
  slug: string | null;
  category: string | null;
  is_wiki_page: boolean;
  created_at: string;
  updated_at: string;
}

const LearningWiki: React.FC = () => {
  const { getAuthToken } = useAuth();
  const { pageSlug } = useParams<{ pageSlug?: string }>();
  const [pages, setPages] = useState<WikiPage[]>([]);
  const [currentPage, setCurrentPage] = useState<WikiPage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMarkdownGuide, setShowMarkdownGuide] = useState(false);
  const [viewMode, setViewMode] = useState<'split' | 'edit' | 'preview'>('split');
  const [backlinks, setBacklinks] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: [] as string[],
    category: '',
    slug: '',
    is_wiki_page: true,
  });

  // Extract wiki links from content ([[page name]])
  const extractWikiLinks = (content: string): string[] => {
    const linkRegex = /\[\[([^\]]+)\]\]/g;
    const links: string[] = [];
    let match;
    while ((match = linkRegex.exec(content)) !== null) {
      links.push(match[1].trim());
    }
    return [...new Set(links)]; // Remove duplicates
  };

  // Find backlinks (pages that link to current page)
  const findBacklinks = useCallback((pageTitle: string) => {
    if (!pageTitle) return [];
    const links: string[] = [];
    pages.forEach((page) => {
      const wikiLinks = extractWikiLinks(page.content);
      if (wikiLinks.some((link) => link.toLowerCase() === pageTitle.toLowerCase())) {
        links.push(page.title || page.slug || 'Untitled');
      }
    });
    return links;
  }, [pages]);

  // Generate slug from title
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Fetch all wiki pages
  const fetchPages = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getAuthToken();
      const response = await fetch(`${getApiUrl()}/api/journal?is_wiki_page=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPages(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthToken]);

  // Fetch a specific page by slug or title
  const fetchPage = useCallback(async (slugOrTitle: string) => {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${getApiUrl()}/api/journal/wiki/${slugOrTitle}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.page) {
          setCurrentPage(data.page);
          setFormData({
            title: data.page.title || '',
            content: data.page.content || '',
            tags: data.page.tags || [],
            category: data.page.category || '',
            slug: data.page.slug || '',
            is_wiki_page: true,
          });
          setIsEditing(false);
          const links = findBacklinks(data.page.title || data.page.slug || '');
          setBacklinks(links);
        }
      }
    } catch (error) {
      console.error('Error fetching page:', error);
    }
  }, [getAuthToken, findBacklinks]);

  // Create new page
  const handleNewPage = () => {
    setCurrentPage(null);
    setFormData({
      title: '',
      content: '',
      tags: [],
      category: '',
      slug: '',
      is_wiki_page: true,
    });
    setIsEditing(true);
    setViewMode('edit');
    setBacklinks([]);
  };

  // Save page
  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Please give your page a title!');
      return;
    }

    if (!formData.content.trim()) {
      alert('Please write something in your page!');
      return;
    }

    setIsSaving(true);
    try {
      const token = await getAuthToken();
      const slug = formData.slug || generateSlug(formData.title);
      
      // If this is a new page (currentPage is null), check if slug already exists locally
      // This provides early feedback, but backend will also check
      if (!currentPage) {
        const existingPage = pages.find(
          (p) => p.slug?.toLowerCase() === slug.toLowerCase() && p.is_wiki_page
        );
        if (existingPage) {
          const useExisting = window.confirm(
            `A page with the title "${formData.title}" already exists (slug: "${slug}"). ` +
            `Would you like to edit that page instead, or create a new one with a different title?`
          );
          if (useExisting) {
            // Load the existing page for editing
            await fetchPage(existingPage.slug || existingPage.title || '');
            setIsSaving(false);
            return;
          } else {
            // User wants to create new page - they'll need to change the title
            alert('Please change the title to create a new page, or edit the existing page.');
            setIsSaving(false);
            return;
          }
        }
      }
      
      const response = await fetch(`${getApiUrl()}/api/journal/wiki`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          slug,
          id: currentPage?.id || null, // Pass ID so backend knows if it's update or create
          is_wiki_page: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPage(data.page);
        setIsEditing(false);
        setViewMode('split');
        fetchPages();
        const links = findBacklinks(data.page.title || data.page.slug || '');
        setBacklinks(links);
      } else {
        const error = await response.json();
        const errorMessage = error.error || 'Failed to save page';
        
        // If it's a duplicate slug error and we have the existing page ID, offer to load it
        if (error.existingPageId && errorMessage.includes('already exists')) {
          const loadExisting = window.confirm(
            `${errorMessage}\n\nWould you like to edit the existing page instead?`
          );
          if (loadExisting) {
            // Fetch the existing page by ID
            const existingPage = pages.find(p => p.id === error.existingPageId);
            if (existingPage) {
              await fetchPage(existingPage.slug || existingPage.title || '');
            }
          }
        } else {
          alert(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error saving page:', error);
      alert('Failed to save page. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Render markdown with wiki link support
  const renderMarkdown = (content: string) => {
    // Custom component to handle wiki links
    const components = {
      a: ({ href, children, ...props }: any) => {
        // Handle wiki links (internal links starting with /wiki/)
        if (href && href.startsWith('/wiki/')) {
          const pageName = decodeURIComponent(href.replace('/wiki/', ''));
          return (
            <button
              {...props}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                fetchPage(pageName);
              }}
              className="text-purple-600 hover:text-purple-800 underline font-medium bg-transparent border-none p-0 cursor-pointer"
            >
              {children}
            </button>
          );
        }
        // Regular external links
        return (
          <a {...props} href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      },
    };

    // Convert [[wiki links]] to markdown links
    const processedContent = content.replace(
      /\[\[([^\]]+)\]\]/g,
      (match, pageName) => {
        return `[${pageName}](/wiki/${encodeURIComponent(pageName)})`;
      }
    );

    return (
      <ReactMarkdown
        components={components}
        className="prose prose-lg prose-slate max-w-none
          prose-headings:font-bold prose-headings:text-gray-900
          prose-h1:text-3xl prose-h1:mb-4 prose-h1:mt-6
          prose-h2:text-2xl prose-h2:mb-3 prose-h2:mt-5
          prose-h3:text-xl prose-h3:mb-2 prose-h3:mt-4
          prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-purple-600 prose-a:no-underline hover:prose-a:underline prose-a:font-medium
          prose-strong:text-gray-900 prose-strong:font-semibold
          prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
          prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
          prose-li:text-gray-700 prose-li:mb-2
          prose-code:text-purple-600 prose-code:bg-purple-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono
          prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:p-4 prose-pre:overflow-x-auto
          prose-blockquote:border-l-4 prose-blockquote:border-purple-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600
          prose-img:rounded-lg prose-img:shadow-md
          prose-hr:border-gray-300 prose-hr:my-8"
      >
        {processedContent}
      </ReactMarkdown>
    );
  };

  // Filter pages by search
  const filteredPages = pages.filter((page) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (page.title?.toLowerCase().includes(query)) ||
      (page.content?.toLowerCase().includes(query)) ||
      (page.tags?.some((tag) => tag.toLowerCase().includes(query))) ||
      (page.category?.toLowerCase().includes(query))
    );
  });

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // Load page from URL if pageSlug is provided
  useEffect(() => {
    if (pageSlug) {
      fetchPage(decodeURIComponent(pageSlug));
    }
  }, [pageSlug, fetchPage]);

  const markdownGuide = `# Markdown Guide

## Headers
\`\`\`
# H1 - Biggest
## H2 - Big
### H3 - Medium
\`\`\`

## Text Styles
\`\`\`
**bold text**
*italic text*
\`code\`
\`\`\`

## Lists
\`\`\`
- Unordered item
- Another item

1. Ordered item
2. Another item
\`\`\`

## Links
\`\`\`
[Link text](https://example.com)
[[Wiki Page]] - Link to another wiki page!
\`\`\`

## Code Blocks
\`\`\`javascript
function hello() {
  console.log("Hello!");
}
\`\`\`

## Blockquotes
\`\`\`
> This is a quote
\`\`\`

## Wiki Linking
Use double brackets to link to other pages:
\`\`\`
[[React Hooks]]
[[JavaScript Basics]]
\`\`\`
This creates clickable links to other wiki pages!`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="hexagon bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center w-16 h-16">
                <BookMarked className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Learning Wiki</h1>
                <p className="text-gray-600">Your personal knowledge base - learn markdown and organize your learning!</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowMarkdownGuide(!showMarkdownGuide)}
                className="px-4 py-2 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors flex items-center space-x-2"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Markdown Guide</span>
              </button>
              <button
                onClick={handleNewPage}
                className="hexagon-button flex items-center space-x-2 px-6 py-3"
              >
                <Plus className="w-5 h-5" />
                <span>New Page</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Markdown Guide Modal */}
        {showMarkdownGuide && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 glass-effect rounded-2xl p-6 border-2 border-purple-300"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Markdown Syntax Guide</h3>
              <button
                onClick={() => setShowMarkdownGuide(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="prose max-w-none">
                <ReactMarkdown>{markdownGuide}</ReactMarkdown>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                <pre className="whitespace-pre-wrap">{markdownGuide}</pre>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Page List */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="glass-effect rounded-2xl p-6 sticky top-4">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search pages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                  </div>
                ) : filteredPages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery ? 'No pages found' : 'No pages yet. Create your first page!'}
                  </div>
                ) : (
                  filteredPages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => fetchPage(page.slug || page.title || '')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                        currentPage?.id === page.id
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                          : 'bg-white hover:bg-purple-50 text-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium truncate">
                          {page.title || 'Untitled'}
                        </span>
                      </div>
                      {page.category && (
                        <div className="mt-1 text-xs opacity-75 flex items-center space-x-1">
                          <Hash className="w-3 h-3" />
                          <span>{page.category}</span>
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </motion.div>

          {/* Main Editor/Viewer */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3"
          >
            <div className="glass-effect rounded-2xl p-6">
              {!currentPage && !isEditing ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    Select a page from the sidebar or create a new one!
                  </p>
                  <button
                    onClick={handleNewPage}
                    className="hexagon-button inline-flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create Your First Page</span>
                  </button>
                </div>
              ) : (
                <>
                  {/* View Mode Toggle */}
                  {isEditing && (
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => setViewMode('edit')}
                          className={`px-4 py-2 rounded-md transition-colors ${
                            viewMode === 'edit'
                              ? 'bg-white shadow-sm text-purple-600'
                              : 'text-gray-600'
                          }`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setViewMode('split')}
                          className={`px-4 py-2 rounded-md transition-colors ${
                            viewMode === 'split'
                              ? 'bg-white shadow-sm text-purple-600'
                              : 'text-gray-600'
                          }`}
                        >
                          Split
                        </button>
                        <button
                          onClick={() => setViewMode('preview')}
                          className={`px-4 py-2 rounded-md transition-colors ${
                            viewMode === 'preview'
                              ? 'bg-white shadow-sm text-purple-600'
                              : 'text-gray-600'
                          }`}
                        >
                          Preview
                        </button>
                      </div>
                      <button
                        onClick={handleSave}
                        disabled={isSaving || !formData.title.trim() || !formData.content.trim()}
                        className="hexagon-button flex items-center space-x-2 px-6 py-3 disabled:opacity-50"
                      >
                        <Save className="w-5 h-5" />
                        <span>{isSaving ? 'Saving...' : 'Save Page'}</span>
                      </button>
                    </div>
                  )}

                  {/* Editor */}
                  {isEditing && (viewMode === 'edit' || viewMode === 'split') && (
                    <div className={`${viewMode === 'split' ? 'grid grid-cols-2 gap-4' : ''}`}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Page Title *
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => {
                            const title = e.target.value;
                            setFormData({
                              ...formData,
                              title,
                              slug: formData.slug || generateSlug(title),
                            });
                          }}
                          placeholder="e.g., React Hooks, JavaScript Basics..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-4"
                        />

                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Content (Markdown) *
                        </label>
                        <textarea
                          value={formData.content}
                          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                          placeholder={`# My Page Title

Write your content here using **markdown**!

## Tips:
- Use [[Page Name]] to link to other pages
- Use **bold** and *italic* for emphasis
- Add code with \`code\` or code blocks`}
                          rows={viewMode === 'split' ? 20 : 15}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
                        />

                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Category
                            </label>
                            <input
                              type="text"
                              value={formData.category}
                              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                              placeholder="e.g., React, JavaScript..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Tags (comma-separated)
                            </label>
                            <input
                              type="text"
                              value={formData.tags.join(', ')}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  tags: e.target.value.split(',').map((t) => t.trim()).filter((t) => t),
                                })
                              }
                              placeholder="hooks, state, components..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>

                      {viewMode === 'split' && (
                        <div className="border-l pl-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
                          <div className="bg-white rounded-lg p-4 border border-gray-200 max-h-[600px] overflow-y-auto">
                            {renderMarkdown(formData.content)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Preview/View */}
                  {(viewMode === 'preview' || !isEditing) && (
                    <div>
                      {currentPage && (
                        <>
                          <div className="mb-6">
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">
                              {currentPage.title || 'Untitled'}
                            </h2>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              {currentPage.category && (
                                <span className="flex items-center space-x-1">
                                  <Hash className="w-4 h-4" />
                                  <span>{currentPage.category}</span>
                                </span>
                              )}
                              {currentPage.tags.length > 0 && (
                                <div className="flex items-center space-x-2">
                                  {currentPage.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Backlinks */}
                          {backlinks.length > 0 && (
                            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <LinkIcon className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-900">Pages linking here:</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {backlinks.map((link) => (
                                  <button
                                    key={link}
                                    onClick={() => fetchPage(link)}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors"
                                  >
                                    {link}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="prose max-w-none">
                            {renderMarkdown(currentPage.content)}
                          </div>

                          {!isEditing && (
                            <button
                              onClick={() => {
                                setIsEditing(true);
                                setViewMode('split');
                              }}
                              className="mt-6 hexagon-button inline-flex items-center space-x-2"
                            >
                              <Edit3 className="w-4 h-4" />
                              <span>Edit Page</span>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LearningWiki;

